import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useAuth } from "@/contexts/AuthContext";

// VIB-1179: MenuKey avledes nå fra det sentrale menyregisteret, så typen og
// menyflatene ikke kan drive fra hverandre. Dot-separator-konvensjon beholdes:
// "innstillinger.organisasjon" er sub-key av "innstillinger".
import type { MenuKey } from "@/lib/menu-registry";
import { useIsLeadBooker, withLeadBooker } from "@/hooks/use-lead-booker";
export type { MenuKey };

export interface RoleMenuPermission {
  role: string;
  menu_key: MenuKey;
  can_access: boolean;
  // VIB-721b: sort_order brukes til å bygge sidebar-rekkefølgen dynamisk.
  // Lavere = høyere opp. Sub-keys innenfor samme parent sorteres lokalt.
  sort_order: number;
}

export function useMenuPermissions() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["role-menu-permissions"],
    queryFn: async (): Promise<RoleMenuPermission[]> => {
      const { data, error } = await supabase
        .from("role_menu_permissions")
        .select("role, menu_key, can_access, sort_order");
      if (error) throw error;
      return (data ?? []) as RoleMenuPermission[];
    },
    // Vent på innlogget sesjon. Kalles hooken før sesjonen er klar (fra en
    // provider eller før `AuthGate` vet om brukeren), går spørringen som anon,
    // feiler med 401 og prøves bare én gang til. Da sto tilgangene feilet for
    // resten av økten, og `/` sendte brukeren til Hjelp.
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
}

// VIB-721b: batch-oppdater sort_order for alle keys i en rolle. Brukes
// av drag&drop-UI som rekkefolger hele lista i én operasjon.
export function useReorderMenuPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      role: string;
      ordered: { menu_key: MenuKey; sort_order: number }[];
    }) => {
      // Vi gjør én upsert per rad fordi PostgREST trenger primær-nøkkel
      // (role, menu_key) for å treffe riktig rad — bulk-upsert i samme
      // kall er enklest sekvensielt her uten egen RPC.
      for (const row of input.ordered) {
        const { error } = await supabase
          .from("role_menu_permissions")
          .update({
            sort_order: row.sort_order,
            updated_at: new Date().toISOString(),
          })
          .eq("role", input.role)
          .eq("menu_key", row.menu_key);
        if (error) throw error;
      }
    },
    onSuccess: async () => {
      await invalidateFor(qc, "role_menu_permissions");
    },
  });
}

export function useCanAccessMenu(menuKey: MenuKey): boolean {
  const { activeUser } = useImpersonation();
  const { data } = useMenuPermissions();
  const isLeadBooker = useIsLeadBooker();
  if (!activeUser || !data) return false;
  const match = data.find(
    (p) => p.role === activeUser.role && p.menu_key === menuKey,
  );
  return withLeadBooker(menuKey, match?.can_access ?? false, isLeadBooker);
}

export function useUpdateMenuPermission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { role: string; menu_key: MenuKey; can_access: boolean }) => {
      const { error } = await supabase
        .from("role_menu_permissions")
        .upsert(
          { ...input, updated_at: new Date().toISOString() },
          { onConflict: "role,menu_key" },
        );
      if (error) throw error;
    },
    onSuccess: async () => {
      await invalidateFor(qc, "role_menu_permissions");
    },
  });
}
